import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Input } from "@/components/ui/input";
import { X } from "lucide-react";

export interface EntityRef {
  kind: "contact" | "company";
  id: string;
  label: string;
}

/** Search contacts and companies through the global search endpoint. */
export function EntityPicker({ value, onChange }: { value: EntityRef | null; onChange: (v: EntityRef | null) => void }) {
  const [text, setText] = useState("");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setQ(text.trim()), 250);
    return () => clearTimeout(t);
  }, [text]);

  const { data } = useQuery<{ contacts: any[]; companies: any[] }>({
    queryKey: ["/api/search", q, "qr-picker"],
    queryFn: async () => {
      const r = await fetch(`/api/search?q=${encodeURIComponent(q)}&limit=6`, { credentials: "include" });
      return r.ok ? r.json() : { contacts: [], companies: [] };
    },
    enabled: q.length >= 2,
  });

  if (value) {
    return (
      <div className="flex h-9 items-center justify-between rounded-md border border-input px-3 text-sm">
        <span className="truncate">{value.label}</span>
        <button type="button" onClick={() => onChange(null)} aria-label="Clear" className="text-muted-foreground hover:text-foreground">
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  const options: EntityRef[] = [
    ...(data?.contacts ?? []).map((c) => ({ kind: "contact" as const, id: c.id, label: c.companyName ? `${c.name} · ${c.companyName}` : c.name })),
    ...(data?.companies ?? []).map((c) => ({ kind: "company" as const, id: c.id, label: `${c.name} · ${c.category}` })),
  ];

  return (
    <div className="relative">
      <Input
        placeholder="Search a person or company (optional)"
        value={text}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onChange={(e) => setText(e.target.value)}
      />
      {open && q.length >= 2 && (
        <div className="absolute z-50 mt-1 max-h-64 w-full overflow-y-auto rounded-md border bg-popover text-sm shadow-md">
          {options.length === 0 ? (
            <div className="px-3 py-2 text-muted-foreground">No matches</div>
          ) : (
            options.map((o) => (
              <button
                key={`${o.kind}:${o.id}`}
                type="button"
                className="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left hover:bg-muted"
                onMouseDown={() => { onChange(o); setText(""); setOpen(false); }}
              >
                <span className="truncate">{o.label}</span>
                <span className="shrink-0 text-[11px] text-muted-foreground">{o.kind === "contact" ? "Person" : "Company"}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
